import type { EventSummary } from '@finder/contracts';

/**
 * Card summary. An ICS field dump is not a sentence about the event, so the
 * card omits it. Real sentences pass through unchanged. Display only: the
 * API teaser is left as the server sent it.
 */
export function readableTeaser(teaser: string | null | undefined): string {
  const text = (teaser ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (/^event interval\b/i.test(text)) return '';
  if (
    /^(campus location|campus room|accessibility contact|event types|event tags)\s*:/i.test(text)
  ) {
    return '';
  }
  return text;
}

/**
 * Short description for the detail sheet. Feed field dumps (ICS labels, a
 * trailing "Cost: Free Event Categories…" block) are not the event. The human
 * sentences stay, trimmed to a few lines.
 */
const FIELD_LINE =
  /^(event interval|campus location|campus room|accessibility contact|event types|event tags|link|website|cost)\b/i;

export function leadDescription(raw: string | null | undefined): string {
  const plain = (raw ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\[detail page\]/gi, ' ')
    .replace(/[\u200b\u200c\u200d\ufeff]/g, '');
  const lines = plain
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line && !FIELD_LINE.test(line) && !/^https?:\/\//i.test(line));
  let text = lines.join(' ').replace(/\s+/g, ' ').trim();
  text = text.replace(/\bCost:\s*Free\b[\s\S]*$/i, '').trim();
  if (text.length <= 420) return text;
  const cut = text.slice(0, 420);
  const stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  return (stop > 80 ? cut.slice(0, stop + 1) : cut).trim();
}

/** Human-friendly timing label. */
export function timingLabel(
  event: Pick<EventSummary, 'timingBand' | 'minutesRemaining' | 'startInstant' | 'venueTimeZone'>,
): string {
  if (event.timingBand === 'ongoing') {
    const mins = event.minutesRemaining ?? 0;
    if (mins >= 60) return `Happening now · ${Math.floor(mins / 60)}h ${mins % 60}m left`;
    return `Happening now · ${mins}m left`;
  }
  const when = new Date(event.startInstant).toLocaleString('en-US', {
    timeZone: event.venueTimeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return event.timingBand === 'starting_soon' ? `Starting soon · ${when}` : when;
}

/**
 * Distance label, e.g. "1.2 mi away".
 * A null distance is an unresolved venue. Under 0.1 miles is "Right here" only
 * while GPS is the origin. A typed place uses "At this place" for that same
 * distance.
 */
export function distanceLabel(
  distanceMiles: number | null,
  origin: 'gps' | 'place' = 'place',
): string {
  if (distanceMiles === null) return 'Location unknown';
  if (distanceMiles < 0.1) return origin === 'gps' ? 'Right here' : 'At this place';
  return `${distanceMiles.toFixed(1)} mi away`;
}

/**
 * Extra place label when the pin is not a street address. Unknown locations
 * already read as "Location unknown" from `distanceLabel(null)`.
 */
export function locationPrecisionLabel(
  precision: EventSummary['locationPrecision'] | undefined,
): string | null {
  return precision === 'approximate' ? 'Approximate location' : null;
}

/** Food confidence badge text. */
export function foodLabel(event: Pick<EventSummary, 'foodState'>): string {
  return event.foodState === 'confirmed' ? 'Free food confirmed' : 'Free food unconfirmed';
}

/**
 * Confidence cue on the food badge. Confirmed food reads as fact. Unconfirmed
 * food says so in that word: the listing mentions it, and we have not checked.
 * The amber dot is a second signal; the word is the one that counts.
 */
export interface ConfidenceCue {
  tone: 'confirmed' | 'unconfirmed';
  label: string;
  /** Longer description for title/aria — never a bare color-only signal. */
  description: string;
}

export function confidenceCue(foodState: EventSummary['foodState']): ConfidenceCue {
  return foodState === 'confirmed'
    ? {
        tone: 'confirmed',
        label: 'Confirmed',
        description: 'Free food is confirmed by the source.',
      }
    : {
        tone: 'unconfirmed',
        label: 'Unconfirmed',
        description:
          'Free food is unconfirmed. The listing mentions it, and we have not checked it.',
      };
}

/** Whether to show the food pill (an explicit 'yes', or legacy fallback). */
export function showsFood(event: Pick<EventSummary, 'offers'>): boolean {
  return event.offers.food === 'yes';
}

/** Whether to show the drinks pill. */
export function showsDrink(event: Pick<EventSummary, 'offers'>): boolean {
  return event.offers.drink === 'yes';
}

/**
 * Whether the food/drink split is known at all. When neither is an explicit
 * 'yes' (the DB read model does not persist the split yet), the card degrades
 * to a single honest "Free food & drinks" pill — exactly the graceful
 * degradation the mockup documents.
 */
export function hasOfferSplit(event: Pick<EventSummary, 'offers'>): boolean {
  return event.offers.food === 'yes' || event.offers.drink === 'yes';
}
